import _ from "lodash";

export const CHANNEL_VARIABLE_STATE_CHANGE = "variable-state-change";
export const CHANNEL_CALCULATIONS_UPDATED = "calculations-updated";

export const COMPARISON_OPERATOR = "comparisonOperator";
export const LOGICAL_OPERATOR = "logicalOperator";
export const ARITHMETIC_OPERATOR = "artithmeticOperator";
export const ID_REF = "idRef"; // variable
export const GROUP_REF = "groupRef";
export const VALUE = "value";

const sampleSchema = { // eslint-disable-line
	id: "myCalcId",
	definition:
	{
		type: "logicalOperator",
		value: "and",
		items: [
			{
				type: "comparisonOperator",
				value: "equals",
				items: [
					{
						type: "idRef",
						value: "mySampleId"
					},
					{
						type: "value",
						value: "Some Value"
					}
				]
			},
			{
				type: "comparisonOperator",
				value: "equals",
				items: [
					{
						type: "idRef",
						value: "mySampleId2"
					},
					{
						type: "value",
						value: "Some Other Value"
					}
				]
			}
		]
	}
};

function getVariableIds(scope, variableIds = []) {
	if (scope.definition) {
		getVariableIds(scope.definition, variableIds);
	} if (scope.items) {
		scope.items.forEach(item => {
			getVariableIds(item, variableIds);
		});
	} else if (scope.type === "idRef") {
		variableIds.push(scope.value);
	}
}

// function getGroupds(scope, variableIds = []) {
// 	if (scope.definition) {
// 		getGroupds(scope.definition, variableIds);
// 	} if (scope.items) {
// 		scope.items.forEach(item => {
// 			getGroupds(item, variableIds);
// 		});
// 	} else if (scope.type === "idRef") {
// 		variableIds.push(scope.value);
// 	}
// }

function flattenObjectArray(value) {
	let result = value;
	if (value && _.isArray(value)) {
		result = [];

		// Inspect to see if all values are Objects and flatten
		value.forEach(item => {
			if (_.isObject(item) && item.hasOwnProperty("value")) {
				// Extract value if this is a value bearing object, otherwise we'll just push what was there
				result.push(item.value);
			} else {
				result.push(item);
			}
		});
	}
	return result;
}
/**
 * Encapsulates the variable state and defined calculations, this way external changes don't automatically update the internal state. Explicit API is provided, either
 * via Pub/Sub or Setter functions to update variable state and/or calculations.
 */
export default class Calculator {
	/**
	 * Constructor, provide initial set of calculations
	 * @param {Array<Object>} calculations array of calculations
	 * @param {Object<Object>} initialVariableState map of id to objects containing id and value - extensible for future
	 * @param {Object} pubSubProvider an instance of a publish/subscribe provider (optional)
	 */
	constructor(calculations, initialVariableState = null, pubSubProvider = null, variableGroups = null) {
		this.setCalculations(calculations);
		this.calculationCache = {};
		this.setPubSubProvider(pubSubProvider);
		this.setVariableGroups(variableGroups);
		if (initialVariableState) {
			this.setVariableState(initialVariableState);
		}
	}

	setPubSubProvider(pubSubProvider) {
		this.pubSubProvider = pubSubProvider;
		if (this.pubSubProvider && !this.variableStateChangeSubscriptionChannelToken) {
			this.variableStateChangeSubscriptionChannelToken = this.pubSubProvider.subscribe(CHANNEL_VARIABLE_STATE_CHANGE, (msg, data) => { this.handleVariableStateChange(data); });
		}
	}

	setVariableGroups(variableGroups) {
		this.variableGroups = variableGroups;
	}

	setVariableState(variableState) {
		this.variableState = _.cloneDeep(variableState);

		// See if there are any values that need to be flattened
		if (variableState) {
			_.keys(variableState).forEach(key => {
				let variable = variableState[key];
				if (variable && variable.value && _.isArray(variable.value)) {
					variable.value = flattenObjectArray(variable.value);
				}
				this.variableState[key] = variable;
			});
		}

		return this.handleCalculations();
	}

	updateVariableState(variableState) {
		return this.handleVariableStateChange(variableState);
	}

	handleVariableStateChange(changedState, publishResults = true) {
		const affectedVariables = [];
		const self = this;
		if (!this.variableState) {
			this.variableState = {};
		}
		if (_.isArray(changedState)) {
			changedState.forEach(variable => {
				if (variable && variable.value && _.isArray(variable.value)) {
					variable.value = flattenObjectArray(variable.value);
				}
				self.variableState[variable.id] = variable;
				affectedVariables.push(variable.id);
			});
		} else {
			_.keys(changedState).forEach(key => {
				let variable = changedState[key];
				if (variable && variable.value && _.isArray(variable.value)) {
					variable.value = flattenObjectArray(variable.value);
				}
				self.variableState[key] = variable;
				affectedVariables.push(key);
			});
		}

		return this.handleCalculations(affectedVariables, publishResults);
	}

	/**
	 * Update calculations
	 * @param {*} calculations 
	 */
	setCalculations(calculations) {
		if (!_.isArray(calculations)) {
			throw new Error("Calculations argument is supposed to be an array of calculations");
		}
		this.calculations = calculations;
		this.optimizeCalculationsCache();
	}

	/**
	 * Will extract meta data from calculcations to optimize them for quick lookup
	 */
	optimizeCalculationsCache() {
		// Build a map of calculations first
		this.calcIdToCalc = {};
		this.variableIdToCalcIds = {};
		// console.log("Running optimization");
		this.calculations.forEach(calculation => {
			this.calcIdToCalc[calculation.id] = calculation;
			let involvedVars = [];
			getVariableIds(calculation, involvedVars);
			involvedVars = _.uniq(involvedVars);
			// console.log("Optimize variables", involvedVars);
			if (involvedVars) {
				involvedVars.forEach(involved => {
					if (!this.variableIdToCalcIds[involved]) {
						this.variableIdToCalcIds[involved] = {};
					}
					// console.log("Mapping", involved, calculation.id);
					this.variableIdToCalcIds[involved][calculation.id] = true;
				});
			}
		});


	}

	getCalculationsForAffectedVariables(affectedVariableIds = []) {
		let calculationIds = [];
		affectedVariableIds.forEach(affectedVariableId => {
			if (this.variableIdToCalcIds[affectedVariableId]) {
				calculationIds = _.concat(calculationIds, _.keys(this.variableIdToCalcIds[affectedVariableId]));
			}
		});

		const calculations = [];
		_.uniq(calculationIds).forEach(calculationId => {
			calculations.push(this.calcIdToCalc[calculationId]);
		});
		// console.log("Calculation ids", calculationIds, this.calcIdToCalc, calculations);
		return calculations;
	}

	/**
	 * Either invoked directly or invoked when an update occurs via the variable-state-change 
	 * @param {Array<String>} affectedVariables - array of variables that have been affected by recent changes (optimization)
	 * @param {Object} variableState - Object with properties that match variable names, contain the variables current value(s)
	 */
	handleCalculations(affectedVariables = "all", publishResults = true) {

		let targetCalculations = this.calculations;
		if (affectedVariables !== "all") {
			targetCalculations = this.getCalculationsForAffectedVariables(affectedVariables);
		}
		// console.log("Handle calculations", affectedVariables, targetCalculations);
		const calculationResults = {};
		targetCalculations.forEach(calculationDefinition => {
			if (calculationDefinition) {
				const result = this.calculate(calculationDefinition);
				this.calculationCache[calculationDefinition.id] = result;
				calculationResults[calculationDefinition.id] = result;
			}
		});

		if (publishResults) {
			if (this.pubSubProvider && !_.isEmpty(calculationResults)) {
				this.pubSubProvider.publish(CHANNEL_CALCULATIONS_UPDATED, calculationResults);
			} else if (this.pubSubProvider) {
				this.pubSubProvider.publish(CHANNEL_CALCULATIONS_UPDATED, calculationResults);
			}
		}
		return calculationResults;
	}

	ignoreCalculationDueToEmptyValue(value1, value2, ignoreEmptyValues) {
		let result = false;
		if (ignoreEmptyValues && (((_.isPlainObject(value1) || _.isArray(value1) || _.isString(value1)) && _.isEmpty(value1) || value1 === "") || ((_.isPlainObject(value2) || _.isArray(value2) || _.isString(value2)) && _.isEmpty(value2) || value2 === ""))) {
			result = true;
		}
		return result;
	}

	calculate(calculationDefinition) {

		if (calculationDefinition.definition) {
			return this.calculate(calculationDefinition.definition);
		}

		switch (calculationDefinition.type) {
			case LOGICAL_OPERATOR: {
				switch (_.toLower(calculationDefinition.value)) {
					case "&&":
					case "and": {

						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});
						// console.log("AND EVAL", calculationDefinition, results);
						return eval(_.join(results, " && "));
					}
					case "||":
					case "or": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						return eval(_.join(results, " || "));
					}
				}
				break;
			}
			case COMPARISON_OPERATOR: {

				let ignoreEmptyValues = false;
				if (calculationDefinition.config && calculationDefinition.config.hasOwnProperty("ignoreEmptyValues")) {
					ignoreEmptyValues = !!calculationDefinition.config.ignoreEmptyValues;
				}

				switch (_.toLower(calculationDefinition.value)) {
					case "equals": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error(`An equals comparison requires two parameters, found ${results.length} instead. ${calculationDefinition}`);
						}

						// Make this a little looser so we don't have to deal with strict typing yet
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}

						const str1 = _.toString(results[0]);
						const str2 = _.toString(results[1]);
						let values1 = [];
						let values2 = [];
						if (str1 && str1.indexOf("|") !== -1) {
							// OR Operator
							values1 = str1.split("|");
						} else {
							values1 = [str1];
						}
						if (str2 && str2.indexOf("|") !== -1) {
							// OR Operator
							values2 = str2.split("|");
						} else {
							values2 = [str2];
						}

						let match = false;

						for (let i = 0; i < values1.length; i++) {
							const val1 = values1[i];
							if (values2.indexOf(val1) !== -1) {
								match = true;
								break;
							}
						}

						return results[0] == results[1] || match; // eslint-disable-line
					}
					case "notequals": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error(`A notEquals comparison requires two parameters, found ${results.length} instead. ${calculationDefinition}`);
						}

						// Make this a little looser so we don't have to deal with strict typing yet

						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}

						const str1 = _.toString(results[0]);
						const str2 = _.toString(results[1]);
						let values1 = [];
						let values2 = [];
						if (str1 && str1.indexOf("|") !== -1) {
							// OR Operator
							values1 = str1.split("|");
						} else {
							values1 = [str1];
						}
						if (str2 && str2.indexOf("|") !== -1) {
							// OR Operator
							values2 = str2.split("|");
						} else {
							values2 = [str2];
						}

						let match = false;

						for (let i = 0; i < values1.length; i++) {
							const val1 = values1[i];
							if (values2.indexOf(val1) !== -1) {
								match = true;
								break;
							}
						}

						return results[0] != results[1] && !match; // eslint-disable-line
					}
					case "lessthan": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A less than operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (!_.isNumber(results[0]) || !_.isNumber(results[1])) {
							try {
								const num1 = _.toNumber(results[0]);
								const num2 = _.toNumber(results[1]);
								return num1 < num2;
							} catch (e) {
								return false;
							}
						} else {
							return results[0] < results[1];
						}
					}
					case "lessthanequal": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A less than or equal operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (!_.isNumber(results[0]) || !_.isNumber(results[1])) {
							try {
								const num1 = _.toNumber(results[0]);
								const num2 = _.toNumber(results[1]);
								return num1 <= num2;
							} catch (e) {
								return false;
							}
						} else {
							return results[0] <= results[1];
						}
					}
					case "greaterthan": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A greater than operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (!_.isNumber(results[0]) || !_.isNumber(results[1])) {
							try {
								const num1 = _.toNumber(results[0]);
								const num2 = _.toNumber(results[1]);
								return num1 > num2;
							} catch (e) {
								return false;
							}
						} else {
							return results[0] > results[1];
						}
					}
					case "greaterthanequal": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A greater than or equal operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (!_.isNumber(results[0]) || !_.isNumber(results[1])) {
							try {
								const num1 = _.toNumber(results[0]);
								const num2 = _.toNumber(results[1]);
								return num1 >= num2;
							} catch (e) {
								return false;
							}
						} else {
							return results[0] >= results[1];
						}
					}
					case "in": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2 || !_.isArray(results[1])) {
							throw new Error("An in operation requires two parameters and the second parameter must be an Array.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						return _.indexOf(results[1], results[0]) !== -1;
					}
					case "notin": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2 || !_.isArray(results[1])) {
							throw new Error("An in operation requires two parameters and the second parameter must be an Array.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						return _.indexOf(results[1], results[0]) === -1;
					}
					case "startswith": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A startsWith operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						return _.startsWith(results[0], results[1]);
					}
					case "endswith": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("An endsWith operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						return _.endsWith(results[0], results[1]);
					}
					case "containsatleastoneof": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A containsatleastoneof operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (_.isEmpty(results[1])) {
							return false;
						}


						const coll = _.isArray(results[0]) ? results[0] : _.isString(results[0]) && results[0].indexOf(",") !== -1 ? results[0].split(",") : results[0];

						if (_.isString(results[1]) && results[1].indexOf(",") !== -1) {
							let result = false;
							results[1].split(",").forEach(part => {
								if (!result) {
									result = coll.indexOf(part) !== -1;
								}
							});
							return result;
						} else {
							return coll.indexOf(results[1]) !== -1;
						}
					}
					case "containsexactly": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A containsexactly operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (_.isEmpty(results[1])) {
							return false;
						}

						const coll = _.isArray(results[0]) ? results[0] : _.isString(results[0]) && results[0].indexOf(",") !== -1 ? results[0].split(",") : results[0];

						if (_.isString(results[1]) && results[1].indexOf(",") !== -1) {
							const parts = results[1].split(",");
							return _.isEmpty(_.xor(coll, parts));
						} else {
							return coll.length === 1 && coll[0] === results[1];
						}
					}
					case "contains":
					case "containsallof": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("A contains/containsallof operation requires two parameters.");
						}

						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}

						if (_.isEmpty(results[1])) {
							return false;
						}

						const coll = _.isArray(results[0]) ? results[0] : _.isString(results[0]) && results[0].indexOf(",") !== -1 ? results[0].split(",") : results[0];

						if (_.isString(results[1]) && results[1].indexOf(",") !== -1) {
							const parts = results[1].split(",");
							const expectedHits = parts.length;
							let hits = 0;
							parts.forEach(part => {
								if (coll.indexOf(part) !== -1) {
									hits++;
								}
							});
							return expectedHits === hits;
						} else {
							return coll.indexOf(results[1]) !== -1;
						}
					}
					case "containsnoneof": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});

						if (results.length !== 2) {
							throw new Error("An containsnoneof operation requires two parameters.");
						}
						if (this.ignoreCalculationDueToEmptyValue(results[0], results[1], ignoreEmptyValues)) {
							return false;
						}
						if (_.isEmpty(results[1])) {
							return true;
						}
						const coll = _.isArray(results[0]) ? results[0] : _.isString(results[0]) && results[0].indexOf(",") !== -1 ? results[0].split(",") : results[0];

						if (_.isString(results[1]) && results[1].indexOf(",") !== -1) {
							let result = true;

							results[1].split(",").forEach(part => {
								if (result) {
									result = coll.indexOf(part) === -1;
								}
							});
							return result;
						} else {
							return coll.indexOf(results[1]) === -1;
						}

					}
					default: {
						throw new Error("Unknown operation: " + calculationDefinition.value);
					}
				}
			}
			case ARITHMETIC_OPERATOR: {
				switch (_.toLower(calculationDefinition.value)) {
					case "add": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});
						let result = 0;
						if (results.length > 0) {
							try {
								results.forEach(_result => {
									result += !_.isNumber(_result) ? _.toNumber(_result) : _result;
								});
							} catch (e) {
								console.error(`Failed to perform add operation on values ${results}, reason:`, e);
							}
						}

						return result;
					}
					case "subtract": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});
						let result = NaN;
						if (results.length > 0) {
							result = !_.isNumber(results[0]) ? _.toNumber(results[0]) : results[0];
							try {
								results.forEach((_result, idx) => {
									if (idx > 0) {
										result -= !_.isNumber(_result) ? _.toNumber(_result) : _result;
									}
								});
							} catch (e) {
								console.error(`Failed to perform add operation on values ${results}, reason:`, e);
							}
						} else {
							result = 0;
						}
						return result;
					}
					case "multiply": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});
						let result = NaN;
						if (results.length > 0) {
							result = !_.isNumber(results[0]) ? _.toNumber(results[0]) : results[0];
							try {
								results.forEach((_result, idx) => {
									if (idx > 0) {
										result *= !_.isNumber(_result) ? _.toNumber(_result) : _result;
									}
								});
							} catch (e) {
								console.error(`Failed to perform add operation on values ${results}, reason:`, e);
							}
						} else {
							result = 0;
						}
						return result;
					}
					case "divide": {
						const results = calculationDefinition.items.map(subCalcDefinition => {
							return this.calculate(subCalcDefinition);
						});
						let result = NaN;
						if (results.length > 0) {
							result = !_.isNumber(results[0]) ? _.toNumber(results[0]) : results[0];
							try {
								results.forEach((_result, idx) => {
									if (idx > 0) {
										result /= !_.isNumber(_result) ? _.toNumber(_result) : _result;
									}
								});
							} catch (e) {
								console.error(`Failed to perform add operation on values ${results}, reason:`, e);
							}
						} else {
							result = 0;
						}
						return result;
					}
					default: {
						throw new Error("Unknown operation: " + calculationDefinition.value);
					}
				}
			}
			case ID_REF: {
				return this.getVariableValue(calculationDefinition.value);
			}
			case VALUE: {
				return flattenObjectArray(calculationDefinition.value);
			}
			case GROUP_REF: {
				return this.getGroupValues(calculationDefinition.value);
			}
		}
	}

	getGroupValues(groupName) {
		let result = null;
		if (this.variableGroups && this.variableGroups[groupName]) {
			result = [];
			_.keys(this.variableGroups[groupName]).forEach(varName => {
				result.push(this.getVariableValue(varName));
			});
		}
		return result;
	}

	getVariableValue(variableName) {
		return this.variableState[variableName] && this.variableState[variableName].hasOwnProperty("value") ? this.variableState[variableName].value : null;
	}

}